"use client";

import { forwardRef, useCallback, useMemo } from "react";
import {
  Label as AriaLabel,
  Slider as AriaSlider,
  SliderOutput,
  SliderThumb,
  SliderTrack,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";

import type { ReactNode } from "react";
import type { SliderProps as AriaSliderProps } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";

// Slider - A slider allows a user to select a value from within a range.
//
// Built on React Aria Components Slider:
// https://react-spectrum.adobe.com/react-aria/Slider.html

const sliderVariants = tv({
  slots: {
    base: "flex w-full flex-col gap-2",
    labelWrapper: "flex justify-between",
    label: "text-sm font-medium text-gray-700 dark:text-gray-300",
    output: "text-sm text-gray-500 dark:text-gray-400",
    trackContainer: "relative flex w-full items-center px-2",
    track: ["relative h-1.5 w-full cursor-pointer rounded-full", "bg-gray-200 dark:bg-gray-700"],
    fill: ["absolute h-full rounded-full", "bg-blue dark:bg-blue-400"],
    thumb: [
      "top-1/2 size-4 cursor-pointer rounded-full",
      "bg-white shadow-md ring-2 ring-blue",
      "outline-none transition-transform",
      "hover:scale-110",
      "focus-visible:ring-4 focus-visible:ring-blue/50",
      "dragging:scale-110 dragging:ring-4 dragging:ring-blue/50",
    ],
    marksContainer: "relative mt-1 w-full",
    mark: "absolute -translate-x-1/2 text-xs text-gray-500 dark:text-gray-400",
    markDot: [
      "absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full",
      "bg-gray-300 dark:bg-gray-600",
    ],
  },
  variants: {
    orientation: {
      horizontal: {
        base: "flex-col",
        track: "h-1.5 w-full",
        fill: "h-full",
        marksContainer: "mt-1",
      },
      vertical: {
        base: "h-full w-auto flex-row items-center",
        trackContainer: "h-full flex-col",
        track: "h-full w-1.5",
        fill: "bottom-0 w-full",
        thumb: "left-1/2",
        marksContainer: "ml-2 h-full",
        mark: "translate-y-1/2",
      },
    },
    isDisabled: {
      true: {
        base: "opacity-50",
        track: "cursor-not-allowed",
        thumb: "cursor-not-allowed",
      },
    },
    size: {
      sm: {
        track: "h-1",
        thumb: "size-3",
      },
      default: {
        track: "h-1.5",
        thumb: "size-4",
      },
      lg: {
        track: "h-2",
        thumb: "size-5",
      },
    },
  },
  compoundVariants: [
    {
      orientation: "vertical",
      size: "sm",
      class: { track: "h-full w-1" },
    },
    {
      orientation: "vertical",
      size: "default",
      class: { track: "h-full w-1.5" },
    },
    {
      orientation: "vertical",
      size: "lg",
      class: { track: "h-full w-2" },
    },
  ],
  defaultVariants: {
    orientation: "horizontal",
    isDisabled: false,
    size: "default",
  },
});

export type SliderMark = {
  value?: number;
  label?: ReactNode;
};

export type SliderMarks = Record<number | string, ReactNode | SliderMark>;

export interface SliderProps
  extends Omit<AriaSliderProps<number | number[]>, "children">,
    VariantProps<typeof sliderVariants> {
  /** Label text for the slider */
  label?: string;
  /** Custom class names for different slots */
  classNames?: Partial<{
    base: string;
    label: string;
    output: string;
    track: string;
    fill: string;
    thumb: string;
    marksContainer: string;
    mark: string;
  }>;
  /**
   * Marks on the slider track.
   * Can be an object mapping values to labels: { 0: 'Off', 50: 'Mid', 100: 'Full' }
   */
  marks?: SliderMarks;
  /** Whether to show the current value next to the label */
  showOutput?: boolean;
  /** Format function for the output value */
  formatOutput?: (value: number | number[]) => string;
  /**
   * Callback fired when the value changes (during dragging).
   * For antd compatibility - maps to onChange.
   */
  onValueChange?: (value: number | number[]) => void;
  /**
   * Callback fired when the user finishes dragging.
   * For antd compatibility with onAfterChange.
   */
  onValueChangeEnd?: (value: number | number[]) => void;
  /**
   * Whether the slider is vertical.
   * Convenience prop that sets orientation="vertical".
   */
  vertical?: boolean;
  /**
   * Alias for isDisabled to match antd API.
   */
  disabled?: boolean;
}

export const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      className,
      classNames,
      label,
      marks,
      showOutput = false,
      formatOutput,
      onValueChange,
      onValueChangeEnd,
      orientation: orientationProp,
      vertical,
      size,
      isDisabled: isDisabledProp,
      disabled,
      minValue = 0,
      maxValue = 100,
      step = 1,
      defaultValue,
      value,
      onChange,
      onChangeEnd,
      ...props
    },
    ref
  ) => {
    const orientation = vertical ? "vertical" : orientationProp || "horizontal";
    const isDisabled = isDisabledProp ?? disabled ?? false;

    const slots = useMemo(
      () => sliderVariants({ orientation, isDisabled, size }),
      [orientation, isDisabled, size]
    );

    // Determine if this is a range slider (array value)
    const isRange = Array.isArray(value) || Array.isArray(defaultValue);

    // Normalize value for single slider compatibility
    const normalizedValue = useMemo(() => {
      if (value !== undefined) {
        return Array.isArray(value) ? value : [value];
      }
      return undefined;
    }, [value]);

    const normalizedDefaultValue = useMemo(() => {
      if (defaultValue !== undefined) {
        return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
      }
      return undefined;
    }, [defaultValue]);

    // Handle value changes
    const handleChange = useCallback(
      (newValue: number[]) => {
        const outputValue = isRange ? newValue : newValue[0];
        onChange?.(newValue);
        onValueChange?.(outputValue as number | number[]);
      },
      [isRange, onChange, onValueChange]
    );

    const handleChangeEnd = useCallback(
      (newValue: number[]) => {
        const outputValue = isRange ? newValue : newValue[0];
        onChangeEnd?.(newValue);
        onValueChangeEnd?.(outputValue as number | number[]);
      },
      [isRange, onChangeEnd, onValueChangeEnd]
    );

    // Format marks for display
    const markElements = useMemo(() => {
      if (!marks) return null;

      return Object.entries(marks).map(([key, markValue]) => {
        const numKey = parseFloat(key);
        const percent = ((numKey - minValue) / (maxValue - minValue)) * 100;
        const positionStyle =
          orientation === "horizontal" ? { left: `${percent}%` } : { bottom: `${percent}%` };

        let labelContent: ReactNode;
        if (typeof markValue === "object" && markValue !== null && "label" in markValue) {
          labelContent = (markValue as SliderMark).label;
        } else {
          labelContent = markValue as ReactNode;
        }

        return (
          <span key={key} className={cn(slots.mark(), classNames?.mark)} style={positionStyle}>
            {labelContent}
          </span>
        );
      });
    }, [marks, minValue, maxValue, orientation, slots, classNames?.mark]);

    // Render thumbs
    const thumbCount = isRange
      ? (normalizedValue?.length ?? normalizedDefaultValue?.length ?? 2)
      : 1;
    const thumbs = useMemo(() => {
      return Array.from({ length: thumbCount }).map((_, index) => (
        <SliderThumb key={index} index={index} className={cn(slots.thumb(), classNames?.thumb)} />
      ));
    }, [thumbCount, slots, classNames?.thumb]);

    // Default output formatter
    const defaultFormatOutput = useCallback((val: number | number[]) => {
      if (Array.isArray(val)) {
        return val.join(" - ");
      }
      return String(val);
    }, []);

    return (
      <AriaSlider
        ref={ref}
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        orientation={orientation}
        isDisabled={isDisabled}
        value={normalizedValue}
        defaultValue={normalizedDefaultValue}
        onChange={handleChange}
        onChangeEnd={handleChangeEnd}
        className={cn(slots.base(), classNames?.base, className)}
        {...props}
      >
        {({ state }) => (
          <>
            {(label || showOutput) && (
              <div className={cn(slots.labelWrapper())}>
                {label && (
                  <AriaLabel className={cn(slots.label(), classNames?.label)}>{label}</AriaLabel>
                )}
                {showOutput && (
                  <SliderOutput className={cn(slots.output(), classNames?.output)}>
                    {(formatOutput ?? defaultFormatOutput)(
                      isRange ? state.values : (state.values[0] ?? 0)
                    )}
                  </SliderOutput>
                )}
              </div>
            )}
            <div className={cn(slots.trackContainer())}>
              <SliderTrack className={cn(slots.track(), classNames?.track)}>
                {/* Fill/progress bar */}
                {!isRange && (
                  <div
                    className={cn(slots.fill(), classNames?.fill)}
                    style={{
                      width:
                        orientation === "horizontal"
                          ? `${(((state.values[0] ?? 0) - minValue) / (maxValue - minValue)) * 100}%`
                          : undefined,
                      height:
                        orientation === "vertical"
                          ? `${(((state.values[0] ?? 0) - minValue) / (maxValue - minValue)) * 100}%`
                          : undefined,
                    }}
                  />
                )}
                {/* Range fill (between two thumbs) */}
                {isRange && state.values.length >= 2 && (
                  <div
                    className={cn(slots.fill(), classNames?.fill)}
                    style={{
                      left:
                        orientation === "horizontal"
                          ? `${(((state.values[0] ?? 0) - minValue) / (maxValue - minValue)) * 100}%`
                          : undefined,
                      width:
                        orientation === "horizontal"
                          ? `${(((state.values[1] ?? 0) - (state.values[0] ?? 0)) / (maxValue - minValue)) * 100}%`
                          : undefined,
                      bottom:
                        orientation === "vertical"
                          ? `${(((state.values[0] ?? 0) - minValue) / (maxValue - minValue)) * 100}%`
                          : undefined,
                      height:
                        orientation === "vertical"
                          ? `${(((state.values[1] ?? 0) - (state.values[0] ?? 0)) / (maxValue - minValue)) * 100}%`
                          : undefined,
                    }}
                  />
                )}
                {thumbs}
              </SliderTrack>
            </div>
            {marks && (
              <div className={cn(slots.marksContainer(), classNames?.marksContainer)}>
                {markElements}
              </div>
            )}
          </>
        )}
      </AriaSlider>
    );
  }
);
Slider.displayName = "Slider";
